import { z } from 'zod';
import { stockUnitSchema } from '../inventory/units.js';
import { nonNegativePaiseSchema } from '../money/paise.js';
import { dateInputSchema, isoDateStringSchema, objectIdSchema } from './common.js';
import { dealForecastTypeSchema, pipelineStageKeySchema } from './pipelines.js';
import { dealLostReasonSchema } from './sales-vocabulary.js';

/**
 * A deal: one piece of business being sold to one person, and where it has got to.
 *
 * WHY THIS EXISTS: one person can be sold several things at once. A wedding lehenga going to
 * quotation and a set of alterations already agreed are two conversations, each on its own ladder,
 * each closing separately — a deal is the record that lets one person hold several at once.
 *
 * A DEAL BELONGS TO A CUSTOMER, ALWAYS. The CRM has one contact record — the customer; somebody
 * who has only enquired is simply a customer with no orders yet — so a deal is raised against a
 * customer and nothing else. (The `lead_id` column the table still carries is legacy: the
 * lead → customer data migration moved every enquiry's deals onto customers, and the column is
 * dropped by a later migration. No API reads or writes it.)
 *
 * A DEAL APPEARS ON THE DEALS BOARD ONLY ONCE IT IS CREATED. There is no implicit deal per
 * customer: somebody nobody has decided is worth quoting is not a deal, and a board padded with one
 * row per contact is the customer list again, wearing a different hat.
 *
 * The stage lives here as a KEY into the deal's pipeline (`schemas/pipelines.ts`) rather than as
 * an enum, because the ladder is the shop's to define. The LABEL is resolved at read time, so a
 * stage renamed in settings reads correctly on every deal at once rather than only on the ones
 * written afterwards — the same rule task category labels follow.
 */

export const DEAL_NAME_MAX_LENGTH = 120;
export const DEAL_NOTES_MAX_LENGTH = 2000;

/**
 * Past this a deal is a quotation, and the form stops being something anybody fills in standing up.
 */
export const MAX_DEAL_LINES = 50;

/* -------------------------------------------------------------------------------------------
 * What is on the deal
 * ---------------------------------------------------------------------------------------- */

/**
 * One line on a deal: either something off the price list, or a garment the shop makes.
 *
 * WHY THE DEAL CARRIES LINES AT ALL when it already has an `amountPaise`. The amount alone says a
 * deal is worth ₹8,000 and never says what for, so the figure is unarguable with: the counter that
 * typed it cannot be checked, and the shop reading the board six weeks later cannot tell a lehenga
 * from four blouses. Naming what is being sold turns a guess into an addition somebody can follow.
 *
 * WHY A LINE IS EITHER A PRODUCT OR A GARMENT, exactly one. These are two different questions a
 * shop answers and neither subsumes the other. A boutique's thread, buttons, lining and zips are
 * PRODUCTS — they carry a price, they were never garments, and no measurement is ever taken of a
 * reel of thread. The stitching itself is a GARMENT — it names a type the shop measures against,
 * and a shop that quotes a one-off "bridal cape" it has never put in its price list must still be
 * able to sell it. Requiring every line to be a catalogue product would make the second impossible;
 * requiring every line to be a garment would make a boutique file its thread as clothing.
 *
 * Exactly one of the two is enforced — see the note on `dealLineInputSchema`.
 *
 * THE LABEL AND THE PRICE ARE FROZEN HERE, whichever kind the line is, exactly as
 * `quotationLineSchema` freezes them and for the same reason: repricing "Blouse stitching" from
 * ₹850 to ₹950, or renaming the garment type "Blouse" to "Blouse (lined)", is an ordinary Tuesday
 * edit in settings — and it must not silently rewrite what every open deal on the board is worth or
 * says. The id and the key stay so a line can still be traced back to what it was copied from: they
 * are provenance, not pointers the deal reads through.
 *
 * `unitPricePaise` IS EDITABLE ON THE LINE, and that is not a hole in the price list. A catalogue
 * price is what the shop asks; a deal is what it expects to get, and "we do her lehenga at 12k
 * because she is bringing her sister" is the single most common thing a counter says. The
 * catalogue's figure is the DEFAULT a product line is created with, never a ceiling — and a garment
 * line has no catalogue figure at all, so its price is always somebody's decision.
 */
export const dealLineSchema = z.object({
  /**
   * Which catalogue line this was copied from, on a product line. Null on a garment line.
   * Exactly one of this and `garmentTypeKey` is set — see above.
   */
  productId: objectIdSchema.nullable(),
  /**
   * Which garment type this line sells, on a garment line. Null on a product line.
   *
   * The KEY is stored and the label frozen beside it, rather than resolved at read time the way
   * `stageLabel` is. A stage label is resolved because a board must show the ladder as it stands
   * today; a deal line is a figure the shop has already counted, and re-reading its label would let
   * a settings rename rewrite the history of what was sold.
   */
  garmentTypeKey: z.string().nullable(),
  /**
   * What the line is called: the product's name, or the garment type's label. Frozen at the moment
   * it was picked, and ONE field for both kinds so that every reader — card, board, totals, the
   * quotation carry-over — renders a line without first asking which sort it is.
   */
  name: z.string(),
  /** The shop's reference code. Products only; always null on a garment line. */
  code: z.string().nullable(),
  quantity: z.number().int().positive(),
  /** Frozen likewise, and freely edited on the line — see the note above. */
  unitPricePaise: nonNegativePaiseSchema,
  /**
   * `quantity × unitPricePaise`, computed on the server so no screen has to reproduce the
   * arithmetic — the rule every priced line in this codebase follows.
   */
  linePaise: nonNegativePaiseSchema,
});
export type DealLine = z.infer<typeof dealLineSchema>;

/**
 * Picking one. The client says WHICH thing and how many; the server fetches the rest.
 *
 * The name and the code are deliberately NOT accepted from the client. They are what the deal will
 * be read by months later, and a client that could supply them could file a line saying "Bridal
 * lehenga" against the id of a saree fall — with nothing on the record to show which was meant.
 *
 * `superRefine` rather than `z.discriminatedUnion`, matching `createProfileSchema` next door: a
 * union reports every branch failing at once and reads as nonsense at the counter, and there is no
 * discriminant field on this body to switch on. The two checks below say the two things a person
 * standing at the counter would actually be told.
 */
export const dealLineInputSchema = z
  .object({
    productId: objectIdSchema.optional(),
    garmentTypeKey: z.string().min(1).optional(),
    quantity: z.number().int().positive().max(999).default(1),
    /**
     * What this deal is charging.
     *
     * On a PRODUCT line, omitted means "whatever the catalogue says today", which is what picking a
     * product off a list plainly means; sent means the counter has agreed a different figure. The
     * server still requires it for a product priced ON THE PIECE, because that is a catalogue line
     * with no figure to copy — see `resolveDealLines`.
     *
     * On a GARMENT line it is always required, and the check below is where that is said rather
     * than in the service, so the form and the API refuse the same thing for the same reason. A
     * garment type is a set of measurements, not a price list; there is nothing behind it to copy.
     */
    unitPricePaise: nonNegativePaiseSchema.nullish(),
  })
  .superRefine((input, ctx) => {
    if (input.productId && input.garmentTypeKey) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['productId'],
        message: 'A line sells a product or a garment, not both',
      });
    }
    if (!input.productId && !input.garmentTypeKey) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['productId'],
        message: 'Say what this line sells',
      });
    }
    if (input.garmentTypeKey && input.unitPricePaise == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['unitPricePaise'],
        message: 'Say what this garment costs — the price list has no figure for it',
      });
    }
  });
export type DealLineInput = z.input<typeof dealLineInputSchema>;

/** What a set of lines comes to. One implementation, used by the server and by the form. */
export function dealLinesTotalPaise(
  lines: readonly { quantity: number; unitPricePaise: number }[],
): number {
  return lines.reduce((total, line) => total + line.quantity * line.unitPricePaise, 0);
}

/**
 * One deal as every screen renders it.
 *
 * Generous with denormalised names — `customerName`, `pipelineName`, `stageLabel`, `ownerName` — for
 * the reason the follow-up queue is: the board draws several hundred cards across six columns, and
 * a card that needs a second request to find out whose deal it is renders blank for as long as
 * that takes on a shop's connection.
 */
export const dealSchema = z.object({
  id: objectIdSchema,

  /**
   * The customer this piece of business is for. NEVER NULL: a deal is raised against a customer
   * and only a customer, and a deal whose customer cannot be read is not rendered at all (see
   * `toDealView`).
   *
   * The name and number are resolved at read time rather than frozen, so the card on the board
   * says what the customer book says today — the board draws several hundred cards, and a card
   * that needed a second request to find out whose deal it is would render blank while it waited.
   */
  customerId: objectIdSchema,
  customerName: z.string(),
  customerPhone: z.string(),

  /** What is being sold, in the shop's words — "wedding lehenga", "uniform order, 40 pieces". */
  name: z.string(),

  pipelineId: objectIdSchema,
  pipelineName: z.string(),

  stageKey: z.string(),
  /**
   * Resolved from the pipeline at read time. Null when the stage was deleted from the ladder out
   * from under this deal — rare, guarded against in `updatePipeline`, and rendered as "unknown
   * stage" rather than crashing a board.
   */
  stageLabel: z.string().nullable(),
  /** Likewise resolved. `open` is the safe reading when the stage has gone: still ours to work. */
  forecastType: dealForecastTypeSchema,
  probability: z.number().int().min(0).max(100),

  /**
   * What is being sold — products, garments, or both. Empty on a deal nobody itemised, and that
   * stays a perfectly ordinary deal — see `dealLineSchema`.
   */
  lines: z.array(dealLineSchema),

  /**
   * What the deal is worth, in paise. Deliberately unlike `quotedTotalPaise` below, which is a
   * cache of a quotation document and refuses client writes.
   *
   * The difference is what each number is FOR. A quoted total is a fact with a document behind it —
   * there is one right answer and the quotation owns it. This is a forecast: what the shop expects
   * to GET, which is exactly the figure a pipeline is read to total. They will differ, and a deal
   * whose amount could not differ from the last quotation would be unable to express "we quoted 8k,
   * they are haggling toward 6k".
   *
   * THREE THINGS CAN AUTHOR THIS FIGURE, and the strongest present one wins:
   *
   *   1. A SENT QUOTATION. Once `quotationId` is set, the quoted total was written here by
   *      `applyQuotationToDeal` and the lines stop deciding — they go on saying what is being sold,
   *      which is still worth reading, but a document has now priced it.
   *   2. THE LINES, when there are some and nothing has been quoted yet: their sum, server-owned,
   *      so the board's column totals stop being somebody's arithmetic.
   *   3. HAND-TYPED, when there are neither. This is how every deal written before the price list
   *      existed still reads.
   *
   * NOTE WHAT CHANGED WHEN QUOTATIONS BEGAN WRITING HERE. This used to say the amount and the lines
   * "cannot disagree because only one of them is ever writable at a time", and that was true while
   * the lines were the only other author. They can disagree now, on purpose: a quoted deal's box
   * goes back to being editable precisely so the haggling above can be recorded, and
   * `amountEditedSinceQuote` is how a reader tells the two apart.
   */
  amountPaise: nonNegativePaiseSchema.nullable(),

  /**
   * The last SENT quotation off this deal, cached here so a card can say where its figure came from
   * without a lookup per row.
   *
   * All four are written together by `applyQuotationToDeal`, only ever inside the transaction that
   * sends the quotation, and never through `updateDealSchema`. A DRAFT quotation writes nothing
   * here: a draft is still being edited, and a forecast that moved every time somebody typed in a
   * draft would make the board flicker for a document nobody has seen yet.
   */
  quotedTotalPaise: nonNegativePaiseSchema.nullable(),
  quotationId: objectIdSchema.nullable(),
  /** The quotation's number, for display — "from QT/26-27/0007". Null until one is sent. */
  quotationNumber: z.string().nullable(),
  quotedAt: isoDateStringSchema.nullable(),

  /** When the shop expects this to close. What the board's "slipping" reading is measured from. */
  expectedCloseAt: isoDateStringSchema.nullable(),

  /**
   * Whose deal it is. Defaults to the CUSTOMER's assignee when the request names nobody, then
   * travels independently of it.
   */
  ownerUserId: z.string().nullable(),
  ownerName: z.string().nullable(),

  notes: z.string().nullable(),

  /**
   * Why it was lost — one of `DEAL_LOST_REASONS`, a closed list so "we lose on price" is a figure
   * the pipeline report can group.
   */
  lostReason: dealLostReasonSchema.nullable(),
  /** Stamped when the deal reached an ending; cleared if it is moved back to an open stage. */
  closedAt: isoDateStringSchema.nullable(),

  /**
   * Whether somebody has moved the worth since the quotation set it — "we quoted 8k, they are
   * haggling toward 6k", said as a boolean a card can render.
   *
   * DERIVED IN THE VIEW, NOT STORED, and that is the point of it. A stored flag would need clearing
   * on the one edit nobody thinks about — typing the quoted figure back in — and would drift the
   * first time any other path touched `amountPaise`. Comparing the two numbers cannot drift: it is
   * false whenever they agree, whoever made them agree.
   */
  amountEditedSinceQuote: z.boolean(),

  createdAt: isoDateStringSchema,
  updatedAt: isoDateStringSchema,
});
export type Deal = z.infer<typeof dealSchema>;

export const dealListSchema = z.object({
  items: z.array(dealSchema),
});
export type DealList = z.infer<typeof dealListSchema>;

/**
 * Creating one.
 *
 * `pipelineId` and `stageKey` are both optional, and that pairing is the whole ergonomics of the
 * feature: the shop's default pipeline and its first OPEN stage are filled in server-side, so
 * "New deal" on a customer can be a name and nothing else. A shop that runs two ladders picks
 * one; a shop that runs one never sees the question.
 *
 * WHO IT IS FOR: `customerId`, REQUIRED. `POST /deals` reads it from the body (the pipeline board
 * picks the customer in its form); `POST /customers/:customerId/deals` sets it from the path,
 * overriding whatever the body said. There is no other owner — a `leadId` is refused (the schema is
 * strict), because leads no longer exist.
 */
export const createDealSchema = z
  .object({
    customerId: objectIdSchema,
    name: z.string().trim().min(1, 'Name the deal').max(DEAL_NAME_MAX_LENGTH),
    pipelineId: objectIdSchema.optional(),
    /**
     * Any stage of the chosen pipeline, ENDINGS INCLUDED.
     *
     * Creating straight into "Closed with Sale" is deliberately allowed, because a shop typing up
     * what happened last week is doing something ordinary — the same thing Bigin allows — and
     * refusing it would mean creating a fiction and immediately correcting it. A deal created into a
     * closing stage is closed on arrival: `closedAt` is stamped and it does not appear on the
     * working board. See `createDeal`.
     */
    stageKey: pipelineStageKeySchema.optional(),
    /**
     * Required by the server when `stageKey` names a LOSING stage, and ignored otherwise.
     *
     * The rule is the same one `moveDealSchema` enforces, and it is here so creation cannot be used
     * to dodge it: without this, "why do we lose?" would be answerable for every deal that was moved
     * to lost and blank for every deal that was entered as lost, which is the half of the data a
     * shop typing up its backlog would produce.
     */
    lostReason: dealLostReasonSchema.optional(),
    /**
     * What is being sold — products off the price list, garments the shop makes, or both. Optional
     * throughout: itemising is how a shop gets a total it can defend, not a hurdle in front of "they
     * want a lehenga too".
     *
     * SENDING ANY LINES MAKES `amountPaise` THE SERVER'S TO DECIDE — it becomes their sum, and
     * anything sent alongside is ignored rather than refused, because a form that computed the total
     * and posted it too would be two answers to one question with no rule for which wins.
     */
    lines: z.array(dealLineInputSchema).max(MAX_DEAL_LINES).optional(),
    amountPaise: nonNegativePaiseSchema.nullish(),
    expectedCloseAt: dateInputSchema.nullish(),
    ownerUserId: objectIdSchema.nullish(),
    notes: z.string().trim().max(DEAL_NOTES_MAX_LENGTH).nullish(),
  })
  .strict();
export type CreateDealInput = z.input<typeof createDealSchema>;

/**
 * Correcting one. `stageKey` is NOT here — moving a deal is its own verb below, because it is the
 * one edit that can close the deal, and a close reached by PATCH is a close that happens by typo.
 * `pipelineId` is absent for the same reason: moving a deal to another ladder means choosing a
 * stage on it, which is a decision, not a field.
 */
export const updateDealSchema = z
  .object({
    /**
     * Re-filing the deal under a different customer — the counter picked the wrong Priya. A
     * customer and never nobody: there is no null here, because a deal always belongs to one.
     */
    customerId: objectIdSchema.optional(),
    name: z.string().trim().min(1, 'Name the deal').max(DEAL_NAME_MAX_LENGTH).optional(),
    /**
     * The whole set, or absent to leave it alone. An empty array is a real instruction — "these
     * were wrong, take them off" — and it hands `amountPaise` back to whoever is typing.
     *
     * Replaced wholesale rather than patched line by line because a deal carries a handful of lines
     * at most, and per-line ids would buy fiddly PATCH semantics nothing on the screen needs.
     */
    lines: z.array(dealLineInputSchema).max(MAX_DEAL_LINES).optional(),
    /**
     * Honoured when the lines are not the author — that is, on a deal with no lines, or on one a
     * quotation has already priced. Ignored otherwise, for the reason `createDealSchema` gives.
     * See the ladder on `dealSchema.amountPaise`.
     */
    amountPaise: nonNegativePaiseSchema.nullish(),
    expectedCloseAt: dateInputSchema.nullish(),
    ownerUserId: objectIdSchema.nullish(),
    notes: z.string().trim().max(DEAL_NOTES_MAX_LENGTH).nullish(),
  })
  .strict()
  .refine((patch) => Object.keys(patch).length > 0, { message: 'Nothing to update' });
export type UpdateDealInput = z.input<typeof updateDealSchema>;

/**
 * Moving a deal along its ladder — including off the end of it.
 *
 * ONE verb for all three kinds of move. A deal reaching a won stage writes nothing outside itself —
 * the customer already exists — so it simply closes. What is asked for is the REASON when the
 * target stage is a lost one, from `DEAL_LOST_REASONS`: a loss recorded without one is a number
 * nobody can act on.
 *
 * `pipelineId` moves the deal to another ladder in the same act, because the stage key it is
 * moving to only means anything relative to a pipeline.
 */
export const moveDealSchema = z.object({
  stageKey: pipelineStageKeySchema,
  /** Defaults to the deal's current pipeline. Supply it to move ladders. */
  pipelineId: objectIdSchema.optional(),
  /** Required by the server when the target stage is a lost one; ignored otherwise. */
  lostReason: dealLostReasonSchema.optional(),
});
export type MoveDealInput = z.input<typeof moveDealSchema>;

/* -------------------------------------------------------------------------------------------
 * Turning a deal into a quotation
 * ---------------------------------------------------------------------------------------- */

/**
 * A deal's lines, ready to be opened as a quotation draft.
 *
 * WHY THE SERVER BUILDS THIS AND NOT THE DIALOG. The web's product list excludes retired lines and
 * is capped, so a browser-side map from `productId` to a name would silently drop exactly the lines
 * whose products the shop has since stopped selling — the case such a map looks most correct on.
 * Garment labels have the same problem from the other end: they are resolved next to the code that
 * resolves them for `toStoredLines`, so there is one answer to what a garment type is called.
 *
 * WHAT IT DOES NOT CARRY, AND THIS IS THE IMPORTANT PART: fabric. A deal knows nothing about cloth,
 * `linePaiseOf` adds `metres × ratePerMetre` on top of the stitching charge, and in tailoring the
 * cloth is routinely half the bill. So every carried-over line arrives with no fabric at all, and
 * the quotation form has to say so out loud — otherwise a shop supplying the cloth sends a quote
 * for half the job and finds out when the customer holds it up.
 *
 * Quantity and price come off the deal's own FROZEN line and are never re-read from the catalogue.
 * A deal agreed at ₹12,000 carries ₹12,000 into the quotation even if the price list says ₹8,500
 * today — the deal is the record of what was agreed, and re-pricing it here would quietly undo the
 * discount somebody promised.
 */
export const dealQuotationDraftLineSchema = z.object({
  productId: objectIdSchema.nullable(),
  garmentTypeKey: z.string().nullable(),
  /**
   * Resolved as it stands TODAY, unlike the deal's own frozen `name`, because this is about to
   * become a new document — and a quotation should go out saying what the shop calls things now.
   * Falls back to the deal's frozen name when the product or garment type has since gone.
   */
  name: z.string(),
  quantity: z.number().int().positive(),
  unitPricePaise: nonNegativePaiseSchema,
});
export type DealQuotationDraftLine = z.infer<typeof dealQuotationDraftLineSchema>;

export const dealQuotationDraftSchema = z.object({
  dealId: objectIdSchema,
  dealName: z.string(),
  /** Who the quotation is being raised for — the deal's customer. */
  customerId: objectIdSchema,
  customerName: z.string(),
  lines: z.array(dealQuotationDraftLineSchema),
});
export type DealQuotationDraft = z.infer<typeof dealQuotationDraftSchema>;

/* -------------------------------------------------------------------------------------------
 * Turning a deal into an order
 * ---------------------------------------------------------------------------------------- */

/**
 * One garment off a won deal, in the shape the booking screen's item row holds.
 *
 * WHY THIS IS NOT `dealQuotationDraftLineSchema` REUSED. A quotation line can be anything the shop
 * sells; an ORDER ITEM is a garment and nothing else — `orderItemInputSchema` requires a
 * `garmentTypeKey`, because an item is a thing that goes through cutting and stitching and is
 * handed to a karigar. So the two drafts carry different line types on purpose, and the difference
 * is exactly the `.nullable()` that has been taken off the key below: every line in this list can
 * become an item, and the caller does not have to filter.
 *
 * The label rides along because the booking screen writes it onto the item, and re-resolving it
 * from the browser's template list would drop a garment type the shop has since retired — the same
 * argument `dealQuotationDraftSchema` makes about products one section up.
 */
export const dealBookingDraftLineSchema = z.object({
  garmentTypeKey: z.string(),
  garmentTypeLabel: z.string(),
  quantity: z.number().int().positive(),
  /**
   * Off the deal's own frozen line, never re-read from the price list. A lehenga agreed at ₹12,000
   * books at ₹12,000 even where the garments screen says ₹8,500 today, for the reason
   * `dealQuotationDraftSchema` gives: re-pricing here would quietly undo the discount somebody
   * promised at the counter.
   */
  unitPricePaise: nonNegativePaiseSchema,
});
export type DealBookingDraftLine = z.infer<typeof dealBookingDraftLineSchema>;

/** One catalogue product off a won deal, in the shape the booking screen's product row holds. */
export const dealBookingDraftProductLineSchema = z.object({
  productId: objectIdSchema.nullable(),
  name: z.string(),
  unit: stockUnitSchema,
  /** Milli-units. */
  quantity: z.number().int().positive(),
  unitPricePaise: nonNegativePaiseSchema,
});
export type DealBookingDraftProductLine = z.infer<typeof dealBookingDraftProductLineSchema>;

/**
 * A won deal's garments, ready to seed a booking — so nobody retypes what the deal already says.
 *
 * WHAT `skippedLineCount` IS FOR, AND WHY IT IS NOT SILENTLY DROPPED. A deal's lines are garments
 * OR catalogue products, and a product — thread, a set of buttons, a fall — has no garment type
 * and therefore cannot be an order item at all. Dropping those quietly would mean a shop that
 * itemised ₹8,400 of work watching ₹7,900 arrive on the booking screen with nothing to explain the
 * difference, which reads as a bug in the price and gets "fixed" by hand. The count is carried so
 * the screen can say the sentence out loud.
 *
 * FABRIC IS ABSENT for the same reason it is absent from the quotation draft: a deal knows nothing
 * about cloth, and in tailoring the cloth is routinely half the bill. Every seeded row therefore
 * arrives with the fabric untouched at its default, for the counter to fill in.
 */
export const dealBookingDraftSchema = z.object({
  dealId: objectIdSchema,
  dealName: z.string(),
  /** The customer the order will be booked for — the deal's own. */
  customerId: objectIdSchema,
  customerName: z.string(),
  customerPhone: z.string(),
  lines: z.array(dealBookingDraftLineSchema),
  /**
   * The deal's catalogue products, as order product lines. The price is the deal's frozen one, for
   * the reason `dealBookingDraftLineSchema` gives; the quantity is in milli-units, as a product
   * line counts it.
   */
  productLines: z.array(dealBookingDraftProductLineSchema).default([]),
  /**
   * How many of the deal's lines could become neither a garment nor a product line. Zero since
   * product lines arrived on orders; kept so the screen can still say the sentence if one appears.
   */
  skippedLineCount: z.number().int().nonnegative(),
});
export type DealBookingDraft = z.infer<typeof dealBookingDraftSchema>;

/* -------------------------------------------------------------------------------------------
 * The board
 * ---------------------------------------------------------------------------------------- */

/**
 * Which rungs of the ladder the board draws.
 *
 * THREE NAMED VIEWS RATHER THAN A `closed` BOOLEAN. A boolean answers one question badly in both
 * directions — `closed=false` reads as "not closed" rather than "still ours to work" — and the
 * shop's third question, "what did we win this month", has no spelling at all. Three views have
 * one spelling each and the switch on the board reads them straight off.
 */
export const DEAL_BOARD_VIEWS = ['open', 'closed', 'all'] as const;
export type DealBoardView = (typeof DEAL_BOARD_VIEWS)[number];
export const dealBoardViewSchema = z.enum(DEAL_BOARD_VIEWS);

/**
 * What the board opens on: the work still to do.
 *
 * A won deal is not gone — it is on the person, where `listDealsForCustomer` keeps it below the
 * live ones — but it has stopped being something anybody drags, and a board that carries every
 * sale a shop has ever made is a board with a permanent horizontal scroll on the right.
 *
 * Read by BOTH sides from here rather than defaulted in the schema. A `.default()` would make the
 * field required in `DealBoardQuery` (which is `z.infer`, the output type) and every caller that
 * passes a partial query — `useDealBoard(query = {})` among them — would stop compiling for a
 * default it never wanted to state.
 */
export const DEFAULT_DEAL_BOARD_VIEW: DealBoardView = 'open';

export const dealBoardQuerySchema = z.object({
  /** Which ladder to draw. Defaults to the shop's default pipeline. */
  pipelineId: objectIdSchema.optional(),
  /** Free text over the deal name and the name or number of whoever it is for. */
  q: z.string().trim().max(120).optional(),
  ownerUserId: objectIdSchema.optional(),
  /** Narrows to one customer's deals. */
  customerId: objectIdSchema.optional(),
  /** Absent means `DEFAULT_DEAL_BOARD_VIEW`. See the note there for why this is not a default. */
  view: dealBoardViewSchema.optional(),
});
export type DealBoardQuery = z.infer<typeof dealBoardQuerySchema>;

/**
 * One column: the stage, what is in it, and what it comes to.
 *
 * The total is computed server-side rather than summed in the browser because the column is
 * CAPPED — a stage holding four hundred deals ships the first fifty cards, and a footer that
 * totalled only what it was sent would quietly understate the pipeline. `total` counts every deal
 * in the stage; `items` is what fits.
 */
export const dealBoardColumnSchema = z.object({
  stageKey: z.string(),
  stageLabel: z.string(),
  forecastType: dealForecastTypeSchema,
  probability: z.number().int(),
  items: z.array(dealSchema),
  /** Every deal in this stage, including the ones past the cap. */
  total: z.number().int().nonnegative(),
  /** Their summed `amountPaise`, ignoring the ones left blank. */
  valuePaise: nonNegativePaiseSchema,
});
export type DealBoardColumn = z.infer<typeof dealBoardColumnSchema>;

export const dealBoardSchema = z.object({
  pipelineId: objectIdSchema,
  pipelineName: z.string(),
  columns: z.array(dealBoardColumnSchema),
});
export type DealBoard = z.infer<typeof dealBoardSchema>;

/** How many cards a column ships before it starts saying "and N more". */
export const DEAL_BOARD_COLUMN_LIMIT = 50;

/** Open deals only — what "what are we carrying" means on any summary. */
export function isOpenDeal(deal: Deal): boolean {
  return deal.forecastType === 'open';
}
